import { useState } from "react";
import { CalendarDays, Copy, Trash2, X } from "lucide-react";
import { DatePickerInput } from "@/components/foundations/inputs/DatePickerInput/DatePickerInput";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

type Props = {
  selectedIds: number[];
  onComplete: () => void;
};

export default function BulkTaskActionBar({ selectedIds, onComplete }: Props) {
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveDate, setMoveDate] = useState("");
  const utils = trpc.useUtils();
  const selectedCount = selectedIds.length;

  const finish = async (message: string) => {
    await utils.invalidate();
    toast.success(message);
    setMoveDialogOpen(false);
    onComplete();
  };

  const duplicateMutation = trpc.tasks.bulkDuplicate.useMutation({
    onSuccess: data => finish(`${data.count} ${data.count === 1 ? "task" : "tasks"} duplicated`),
    onError: error => toast.error(error.message),
  });
  const moveMutation = trpc.tasks.bulkMoveDay.useMutation({
    onSuccess: data => finish(`${data.count} ${data.count === 1 ? "task" : "tasks"} moved`),
    onError: error => toast.error(error.message),
  });
  const removeMutation = trpc.tasks.remove.useMutation({
    onSuccess: () => finish(`${selectedCount} ${selectedCount === 1 ? "task" : "tasks"} deleted`),
    onError: error => toast.error(error.message),
  });
  const busy = duplicateMutation.isPending || moveMutation.isPending || removeMutation.isPending;

  return (
    <>
      <div className="bulk-task-actions">
        <div className="bulk-task-actions-inner">
          <span className="selection-count">{selectedCount} selected</span>
          <div className="actions">
            <button
              type="button"
              onClick={() => duplicateMutation.mutate({ ids: selectedIds })}
              disabled={selectedCount === 0 || busy}
              aria-label="Duplicate selected tasks"
              title="Duplicate"
            >
              <Copy />
              <span>Duplicate</span>
            </button>
            <button
              type="button"
              onClick={() => setMoveDialogOpen(true)}
              disabled={selectedCount === 0 || busy}
              aria-label="Move selected tasks to another day"
              title="Move day"
            >
              <CalendarDays />
              <span>Move day</span>
            </button>
            <button
              type="button"
              onClick={() => removeMutation.mutate({ ids: selectedIds })}
              disabled={selectedCount === 0 || busy}
              aria-label="Delete selected tasks"
              title="Delete"
            >
              <Trash2 />
              <span>Delete</span>
            </button>
            <button type="button" onClick={onComplete} className="cancel" aria-label="Cancel selection" title="Cancel">
              <X />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className="bulk-move-day-dialog">
          <DialogHeader>
            <DialogTitle>Move {selectedCount === 1 ? "task" : `${selectedCount} tasks`}</DialogTitle>
          </DialogHeader>
          <DatePickerInput value={moveDate} onChange={setMoveDate} onClear={() => setMoveDate("")} />
          <div className="dialog-actions">
            <Button type="button" variant="outline" onClick={() => setMoveDialogOpen(false)}>Cancel</Button>
            <Button type="button" variant="outline" disabled={busy} onClick={() => moveMutation.mutate({ ids: selectedIds, date: null })}>Clear day</Button>
            <Button type="button" disabled={!moveDate || busy} onClick={() => moveMutation.mutate({ ids: selectedIds, date: moveDate })}>Move</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
